/**
 * Derives the headline finding — the one sentence shown above every chart.
 *
 * The point is that an executive should not have to read a funnel to learn what
 * is wrong. This inspects the same data the charts render and states the most
 * important thing about it in plain language.
 *
 * Rules are ordered by severity and the FIRST match wins, so the page always
 * leads with the most serious thing that is true. Each rule returns the
 * evidence it used, so the claim can be checked against the charts below it.
 */

import { humanLabel } from "../../components/analytics/primitives";

const pct = (value, digits = 0) =>
  value === null || value === undefined ? "—" : `${(value * 100).toFixed(digits)}%`;

const count = (value) => Number(value ?? 0).toLocaleString();

/** Sums a field across rows. */
const sumBy = (rows, key) => (rows || []).reduce((total, row) => total + (row[key] ?? 0), 0);

/**
 * Orders funnel steps.
 *
 * The API returns one row per step, already counted distinct across the whole
 * period, so these are read in order rather than summed. Summing per-day rows
 * would count a parent once per active day and overstate every step.
 */
const orderSteps = (rows) =>
  [...(rows || [])]
    .map((row) => ({ order: row.step_order, name: row.step_name, users: row.users ?? 0 }))
    .sort((a, b) => a.order - b.order);

/** The step-to-step transition that loses the most people. */
const biggestDrop = (steps) => {
  let worst = null;
  for (let i = 0; i < steps.length - 1; i += 1) {
    const from = steps[i];
    const to = steps[i + 1];
    if (from.users <= 0) continue;
    const lost = from.users - to.users;
    if (lost > 0 && (!worst || lost > worst.lost)) {
      worst = { from: from.name, to: to.name, lost, rate: to.users / from.users };
    }
  }
  return worst;
};

/**
 * @returns {{tone: 'good'|'warn'|'crit', label: string, headline: string, detail: string}}
 */
export const deriveFinding = ({ summary, funnel }) => {
  const steps = orderSteps(funnel?.creationFunnel);
  const started = steps[0]?.users ?? 0;
  const finished = steps[steps.length - 1]?.users ?? 0;
  const failures = funnel?.uploadFailures || [];
  const failureUsers = sumBy(failures, "affected_users");

  // ---- Nothing to say yet -------------------------------------------------
  if (!summary || summary.total_users === 0) {
    return {
      tone: "warn",
      label: "No data",
      headline: "No analytics events have arrived for this period.",
      detail:
        "Either nobody used the app in this window, or collection is not running in the build people are on. Check Firebase DebugView on a device before assuming the former.",
    };
  }

  // ---- Recording ends without a video -------------------------------------
  // record_cancel carries a `reason` of user or error. Rows collected before
  // that parameter shipped have neither, and are reported as such rather than
  // being attributed to a cause that was never measured.
  const byError = sumBy(funnel?.funnelTrend, "cancelled_by_error");
  const byUser = sumBy(funnel?.funnelTrend, "cancelled_by_user");
  const unattributed = sumBy(funnel?.funnelTrend, "cancelled_unattributed");

  if (byError > 0) {
    return {
      tone: "crit",
      label: "Recording is failing",
      headline: `Recording broke for ${count(byError)} parent${byError === 1 ? "" : "s"}, losing the video they were making.`,
      detail:
        "These are genuine failures, not parents changing their mind — the two are counted separately. The engineering team can find the underlying errors in the app's crash reporting.",
    };
  }

  if (started > 0 && finished === 0 && unattributed > 0) {
    return {
      tone: "warn",
      label: "Reason not recorded",
      headline: `${count(unattributed)} recording${unattributed === 1 ? "" : "s"} ended without producing a video, and we do not yet know why.`,
      detail:
        "These happened before the app started recording whether a parent chose to stop or the recording itself broke. Once the updated app is in people's hands, this will separate into one or the other by itself.",
    };
  }

  if (started > 0 && finished === 0 && byUser > 0) {
    return {
      tone: "warn",
      label: "Parents backing out",
      headline: `${count(byUser)} parent${byUser === 1 ? "" : "s"} started recording and chose to stop.`,
      detail:
        "Recording worked — these were deliberate exits, so this is a question about the experience rather than a fault. Worth looking at what the recording screen asks of a parent at that moment.",
    };
  }

  // ---- Started but nothing finished, with no cancels ----------------------
  if (started > 0 && finished === 0) {
    return {
      tone: "crit",
      label: "Funnel incomplete",
      headline: `${count(started)} parent${started === 1 ? "" : "s"} started the creation funnel and none finished a video.`,
      detail:
        "No cancellations were recorded either, so parents are dropping out somewhere between recording and upload. The funnel below shows which step they stop at.",
    };
  }

  // ---- Uploads failing ----------------------------------------------------
  if (failureUsers > 0) {
    const worst = [...failures].sort(
      (a, b) => (b.affected_users ?? 0) - (a.affected_users ?? 0)
    )[0];
    const share = failureUsers > 0 ? (worst.affected_users ?? 0) / failureUsers : 0;
    const stage = humanLabel(worst.error_stage).toLowerCase();
    return {
      tone: "crit",
      label: "Needs attention",
      headline: `Saving failed for ${count(failureUsers)} parent${failureUsers === 1 ? "" : "s"}, most often during ${stage}.`,
      detail: `${humanLabel(worst.error_stage)} accounts for ${pct(share)} of failures. Each one is a video a parent made and never got. This page names the step that failed; the engineering team can find the underlying errors in the app's crash reporting.`,
    };
  }

  // ---- Nobody activating --------------------------------------------------
  if (summary.total_users >= 5 && summary.activated_users === 0) {
    return {
      tone: "warn",
      label: "No activation",
      headline: `None of the ${count(summary.total_users)} parents on record have completed a video.`,
      detail:
        "Discovery may be working while creation is not. Check where the creation funnel below drops off, and confirm the recording flow works end to end on a device.",
    };
  }

  // ---- Where the funnel leaks --------------------------------------------
  const drop = biggestDrop(steps);
  if (drop && drop.rate < 0.6) {
    return {
      tone: "warn",
      label: "Biggest leak",
      headline: `Most parents are lost between "${drop.from}" and "${drop.to}".`,
      detail: `Only ${pct(drop.rate)} continue past that step — ${count(drop.lost)} people did not. That single transition is where a fix would recover the most videos.`,
    };
  }

  // ---- Reliability guardrail ---------------------------------------------
  if (
    summary.upload_success_rate !== null &&
    summary.upload_success_rate !== undefined &&
    summary.upload_success_rate < 0.95
  ) {
    return {
      tone: "warn",
      label: "Reliability",
      headline: `Only ${pct(summary.upload_success_rate, 1)} of videos are saving successfully, below the 95% we aim for.`,
      detail:
        "Uploads are the last step before a parent has something to show their kid, so failures here cost more than the number suggests.",
    };
  }

  // ---- Healthy ------------------------------------------------------------
  return {
    tone: "good",
    label: "Healthy",
    headline: `${count(summary.activated_users)} of ${count(summary.total_users)} parents have made a video, and uploads are succeeding.`,
    detail: `Activation is running at ${pct(summary.activation_rate, 1)} and no upload failures were recorded in this period. The sections below break that down by where parents came from and what they made.`,
  };
};
